/**
 * Reports module landing page — lists available reports.
 */
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { TrendingUp, Award, ArrowRight, BarChart3, Upload } from 'lucide-react';

const reports = [
  {
    key: 'fp-incentive',
    title: 'Sales Incentive Report (Fully Paid)',
    description: 'From July 2026: incentive calculated only on invoices 100% paid by the end of T+2, with no credit note or write-off. 65% target check + slabs, editable with history.',
    status: 'Live · From Jul 2026',
    icon: Award,
    tone: 'from-emerald-500 to-teal-600',
    path: '/reports/fp-incentive',
    tags: ['Sales', 'Incentive', 'Fully paid'],
  },
  {
    key: 'incentive',
    title: 'Employee Incentive Report (till June 2026)',
    description: 'Monthly incentive payout calculated on actual achieved invoice sales (post credit-note adjustments) + payment received gate.',
    status: 'Live · Auto-synced',
    icon: Award,
    tone: 'from-amber-500 to-orange-600',
    path: '/reports/incentive',
    tags: ['Sales', 'Incentive', 'Monthly'],
  },
  {
    key: 'sales-uploads',
    title: 'Sales Uploads',
    description: 'Public time-bound links for the sales team to upload daily Zoho screenshots — Gemini Vision auto-extracts the rows into consolidated, editable tables (Call Back Leads, Daily Hot, UTR, Hot Lead Distribution).',
    status: 'Live · Vision-OCR',
    icon: Upload,
    tone: 'from-violet-500 to-rose-500',
    path: '/reports/sales-uploads',
    tags: ['Sales', 'Daily', 'Vision OCR'],
  },
  // Future reports can be appended here.
];

export default function ReportsLandingPage() {
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 pb-16">
      <div className="max-w-[1200px] mx-auto px-6 pt-10">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-md">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Reports</h1>
            <p className="text-xs text-slate-500 font-medium mt-0.5">Aggregated, derived & periodic business reports</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
          {reports.map(r => {
            const Icon = r.icon;
            return (
              <Card
                key={r.key}
                className="p-5 border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer bg-white group"
                onClick={() => nav(r.path)}
                data-testid={`report-card-${r.key}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${r.tone} flex items-center justify-center shadow shrink-0`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="text-base font-bold text-slate-900 group-hover:text-indigo-700">{r.title}</h3>
                      <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">{r.status}</Badge>
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed mb-3">{r.description}</p>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {r.tags.map(t => (
                        <Badge key={t} variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200">{t}</Badge>
                      ))}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-500 transition-colors shrink-0 mt-2" />
                </div>
              </Card>
            );
          })}

          {/* Placeholder tile */}
          <Card className="p-5 border-dashed border-slate-300 bg-slate-50/50 flex items-center justify-center text-center min-h-[150px]">
            <div>
              <TrendingUp className="w-7 h-7 text-slate-300 mx-auto mb-2" />
              <p className="text-xs text-slate-500">More reports coming soon…</p>
              <p className="text-[11px] text-slate-400 mt-1">Collection, Placement, Trainer utilisation, …</p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
